"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";

/* The site's ink-trail idea applied to a picture. Pass 1 is ReactiveField's
   decaying trail buffer (ping-pong, quarter res) in element-local UV; pass 2
   re-renders the image refracted through that field: the cursor's wake
   displaces UVs along the trail's slope (with a slight perpendicular curl so
   the smear reads liquid, not lensed), splits RGB along the displacement and
   runs the colors hotter, healing back to the untouched image as the trail
   decays. The canvas samples the already-loaded next/image element, so
   nothing downloads twice and colors match exactly. Renders the plain image
   on coarse pointers, reduced motion, or no WebGL2. */

const TOUCH = {
  warp: 0.45 /* UV displacement per unit of trail slope */,
  swirl: 0.16 /* perpendicular curl fraction of the warp */,
  chroma: 0.26 /* RGB dispersion along the displacement */,
  boost: 0.22 /* saturation push inside the wake */,
  grain: 0.05,
  radius: 0.15 /* trail brush radius in element UV */,
};

const QUAD = `#version 300 es
in vec2 aPos;
out vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const TRAIL_FS = `#version 300 es
precision highp float;
uniform sampler2D uPrev;
uniform vec2 uRes;
uniform float uAspect;
uniform vec2 uPoint;
uniform float uInject;
uniform float uRadius;
in vec2 vUv;
out vec4 frag;
void main() {
  vec2 px = 1.0 / uRes;
  float c = texture(uPrev, vUv).r;
  float blur =
    texture(uPrev, vUv + vec2(px.x, 0.0)).r +
    texture(uPrev, vUv - vec2(px.x, 0.0)).r +
    texture(uPrev, vUv + vec2(0.0, px.y)).r +
    texture(uPrev, vUv - vec2(0.0, px.y)).r;
  float v = mix(c, blur * 0.25, 0.4);
  v = max(v * 0.962 - 0.0012, 0.0);
  vec2 a = vec2(uAspect, 1.0);
  float d = distance(vUv * a, uPoint * a);
  v += uInject * exp(-(d * d) / (uRadius * uRadius));
  frag = vec4(v, 0.0, 0.0, 1.0);
}`;

const COMP_FS = `#version 300 es
precision highp float;
uniform sampler2D uImage;
uniform sampler2D uTrail;
uniform vec2 uRes;
uniform vec2 uTrailRes;
uniform vec2 uCoverScale;
uniform vec2 uCoverOffset;
uniform float uFrame;
uniform float uWarp;
uniform float uSwirl;
uniform float uChroma;
uniform float uBoost;
uniform float uGrain;
in vec2 vUv;
out vec4 frag;

float hash(vec2 p) {
  p = fract(p * vec2(234.34, 435.345));
  p += dot(p, p + 34.23);
  return fract(p.x * p.y);
}

vec2 coverUv(vec2 uv) {
  return uv * uCoverScale + uCoverOffset;
}

void main() {
  float t = clamp(texture(uTrail, vUv).r, 0.0, 1.0);
  float s = smoothstep(0.02, 0.6, t);

  /* Slope of the trail field: the wake refracts along it, curling slightly
     sideways so the gradient drags like wet ink instead of bulging. */
  vec2 px = 1.0 / uTrailRes;
  vec2 g = vec2(
    texture(uTrail, vUv + vec2(px.x, 0.0)).r -
      texture(uTrail, vUv - vec2(px.x, 0.0)).r,
    texture(uTrail, vUv + vec2(0.0, px.y)).r -
      texture(uTrail, vUv - vec2(0.0, px.y)).r);
  vec2 disp = g * uWarp + vec2(-g.y, g.x) * uSwirl;

  /* Dispersion: split the channels along the displacement. */
  vec3 col;
  col.r = texture(uImage, coverUv(vUv + disp * (1.0 + uChroma))).r;
  col.g = texture(uImage, coverUv(vUv + disp)).g;
  col.b = texture(uImage, coverUv(vUv + disp * (1.0 - uChroma))).b;

  /* The wake runs hotter, not damaged: push saturation up inside it. */
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  col = clamp(mix(vec3(lum), col, 1.0 + uBoost * s), 0.0, 1.0);

  float gr = hash(vUv * uRes + fract(uFrame * 0.618) * 71.0) - 0.5;
  col += gr * (uGrain * s + 0.006);

  frag = vec4(col, 1.0);
}`;

export function ReactiveImage({
  src,
  alt,
  sizes,
  priority,
}: {
  src: string;
  alt: string;
  sizes: string;
  priority?: boolean;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [motionOk, setMotionOk] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(
      "(pointer: fine) and (prefers-reduced-motion: no-preference)",
    );
    const update = () => setMotionOk(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!motionOk) return;
    const canvas = canvasRef.current;
    const img = imgRef.current;
    const host = canvas?.parentElement;
    if (!canvas || !img || !host) return;
    const gl = canvas.getContext("webgl2", { antialias: false, alpha: false });
    if (!gl) return;

    const compile = (vsSrc: string, fsSrc: string) => {
      const p = gl.createProgram()!;
      for (const [type, srcCode] of [
        [gl.VERTEX_SHADER, vsSrc],
        [gl.FRAGMENT_SHADER, fsSrc],
      ] as const) {
        const s = gl.createShader(type)!;
        gl.shaderSource(s, srcCode);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS))
          throw new Error(gl.getShaderInfoLog(s) ?? "shader compile failed");
        gl.attachShader(p, s);
      }
      gl.linkProgram(p);
      return p;
    };

    let trailProg: WebGLProgram, compProg: WebGLProgram;
    try {
      trailProg = compile(QUAD, TRAIL_FS);
      compProg = compile(QUAD, COMP_FS);
    } catch {
      return; /* the plain image stays */
    }

    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW,
    );
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    let W = 0,
      H = 0,
      TW = 0,
      TH = 0,
      flip = 0;
    let texs: WebGLTexture[] = [];
    let fbos: WebGLFramebuffer[] = [];
    let imgW = 0,
      imgH = 0,
      ready = false;
    const imageTex = gl.createTexture()!;

    const makeTrailTargets = () => {
      for (const t of texs) gl.deleteTexture(t);
      for (const f of fbos) gl.deleteFramebuffer(f);
      texs = [];
      fbos = [];
      for (let i = 0; i < 2; i++) {
        const tex = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texImage2D(
          gl.TEXTURE_2D,
          0,
          gl.RGBA8,
          TW,
          TH,
          0,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          null,
        );
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        const fbo = gl.createFramebuffer()!;
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.framebufferTexture2D(
          gl.FRAMEBUFFER,
          gl.COLOR_ATTACHMENT0,
          gl.TEXTURE_2D,
          tex,
          0,
        );
        texs.push(tex);
        fbos.push(fbo);
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    };

    let dpr = 1;
    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      W = Math.max(2, Math.round(host.clientWidth * dpr));
      H = Math.max(2, Math.round(host.clientHeight * dpr));
      canvas.width = W;
      canvas.height = H;
      TW = Math.max(2, W >> 2);
      TH = Math.max(2, H >> 2);
      makeTrailTargets();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(host);

    /* object-fit: cover mapping from canvas UV into image UV. */
    const cover = () => {
      const canvasAspect = W / H;
      const imageAspect = imgW / imgH;
      let sx = 1,
        sy = 1;
      if (imageAspect > canvasAspect) sx = canvasAspect / imageAspect;
      else sy = imageAspect / canvasAspect;
      return [sx, sy, (1 - sx) / 2, (1 - sy) / 2] as const;
    };

    const pointer = {
      x: 0.5,
      y: 0.5,
      tx: 0.5,
      ty: 0.5,
      speed: 0,
      inside: false,
    };
    let pulse = 0;
    let energy = 0;
    let lastPX: number | null = null;
    let lastPY = 0;
    let raf = 0;
    let frame = 0;

    const uT: Record<string, WebGLUniformLocation | null> = {};
    const uC: Record<string, WebGLUniformLocation | null> = {};
    for (const name of [
      "uPrev",
      "uRes",
      "uAspect",
      "uPoint",
      "uInject",
      "uRadius",
    ])
      uT[name] = gl.getUniformLocation(trailProg, name);
    for (const name of [
      "uImage",
      "uTrail",
      "uRes",
      "uTrailRes",
      "uCoverScale",
      "uCoverOffset",
      "uFrame",
      "uWarp",
      "uSwirl",
      "uChroma",
      "uBoost",
      "uGrain",
    ])
      uC[name] = gl.getUniformLocation(compProg, name);

    const loop = () => {
      frame++;
      pointer.x += (pointer.tx - pointer.x) * 0.16;
      pointer.y += (pointer.ty - pointer.y) * 0.16;
      pointer.speed *= 0.86;
      pulse *= 0.8;

      const inject = (pointer.inside ? pointer.speed * 0.55 : 0) + pulse * 0.6;
      /* CPU shadow of the buffer's decay so the loop can sleep when calm. */
      energy = Math.max(Math.max(energy * 0.962 - 0.0012, 0), inject);

      gl.bindFramebuffer(gl.FRAMEBUFFER, fbos[1 - flip]);
      gl.viewport(0, 0, TW, TH);
      gl.useProgram(trailProg);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texs[flip]);
      gl.uniform1i(uT.uPrev, 0);
      gl.uniform2f(uT.uRes, TW, TH);
      gl.uniform1f(uT.uAspect, W / H);
      gl.uniform2f(uT.uPoint, pointer.x, pointer.y);
      gl.uniform1f(uT.uInject, inject);
      gl.uniform1f(uT.uRadius, TOUCH.radius + pulse * 0.05);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      flip = 1 - flip;

      const [sx, sy, ox, oy] = cover();
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, W, H);
      gl.useProgram(compProg);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, imageTex);
      gl.uniform1i(uC.uImage, 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, texs[flip]);
      gl.uniform1i(uC.uTrail, 1);
      gl.uniform2f(uC.uRes, W, H);
      gl.uniform2f(uC.uTrailRes, TW, TH);
      gl.uniform2f(uC.uCoverScale, sx, sy);
      gl.uniform2f(uC.uCoverOffset, ox, oy);
      gl.uniform1f(uC.uFrame, frame);
      gl.uniform1f(uC.uWarp, TOUCH.warp);
      gl.uniform1f(uC.uSwirl, TOUCH.swirl);
      gl.uniform1f(uC.uChroma, TOUCH.chroma);
      gl.uniform1f(uC.uBoost, TOUCH.boost);
      gl.uniform1f(uC.uGrain, TOUCH.grain);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      if (!pointer.inside && energy < 0.004 && pulse < 0.01) {
        /* Hide the layer whenever the loop sleeps: an idle visible canvas
           can leave stale compositor tiles behind (seen as a black band on
           scroll). At rest the shader output equals the underlying image,
           so the fade is invisible. */
        canvas.style.opacity = "0";
        raf = 0;
        return;
      }
      raf = requestAnimationFrame(loop);
    };

    const wake = () => {
      if (!ready) return;
      canvas.style.opacity = "1";
      if (!raf) raf = requestAnimationFrame(loop);
    };

    const upload = () => {
      if (ready || !img.naturalWidth) return;
      imgW = img.naturalWidth;
      imgH = img.naturalHeight;
      gl.bindTexture(gl.TEXTURE_2D, imageTex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, img);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(
        gl.TEXTURE_2D,
        gl.TEXTURE_MIN_FILTER,
        gl.LINEAR_MIPMAP_LINEAR,
      );
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      ready = true;
      if (pointer.inside) wake();
    };

    if (img.complete && img.naturalWidth) upload();
    else img.addEventListener("load", upload);

    const onMove = (e: PointerEvent) => {
      const r = host.getBoundingClientRect();
      pointer.tx = (e.clientX - r.left) / r.width;
      pointer.ty = 1 - (e.clientY - r.top) / r.height;
      if (lastPX !== null) {
        const d = Math.hypot(e.clientX - lastPX, e.clientY - lastPY);
        pointer.speed = Math.min(1, pointer.speed + d / 90);
      }
      lastPX = e.clientX;
      lastPY = e.clientY;
      pointer.inside = true;
      wake();
    };
    const onDown = (e: PointerEvent) => {
      const r = host.getBoundingClientRect();
      pointer.tx = pointer.x = (e.clientX - r.left) / r.width;
      pointer.ty = pointer.y = 1 - (e.clientY - r.top) / r.height;
      pulse = 1.2;
      wake();
    };
    const onLeave = () => {
      pointer.inside = false;
      lastPX = null;
    };

    host.addEventListener("pointermove", onMove, { passive: true });
    host.addEventListener("pointerdown", onDown, { passive: true });
    host.addEventListener("pointerleave", onLeave, { passive: true });

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      img.removeEventListener("load", upload);
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerdown", onDown);
      host.removeEventListener("pointerleave", onLeave);
      canvas.style.opacity = "0";
      for (const t of texs) gl.deleteTexture(t);
      for (const f of fbos) gl.deleteFramebuffer(f);
      gl.deleteTexture(imageTex);
      gl.deleteProgram(trailProg);
      gl.deleteProgram(compProg);
      gl.deleteBuffer(buf);
      gl.deleteVertexArray(vao);
    };
  }, [motionOk]);

  return (
    <>
      <Image
        ref={imgRef}
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        priority={priority}
        /* Cover here, not per call site: the /blog plate relied on a parent
           rule it never had and rendered squashed at its 2:1 mobile crop. */
        style={{ objectFit: "cover" }}
      />
      {motionOk && (
        <canvas
          ref={canvasRef}
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            transition: "opacity 400ms var(--oi-ease)",
            pointerEvents: "none",
          }}
        />
      )}
    </>
  );
}
